import React from 'react'
import { Box, ButtonBase, Checkbox, CircularProgress, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Add as AddIcon } from '@material-ui/icons'
import FileNameText from 'src/components/FileNameText'
import { candidateInfo, shortDay } from '../model/candidates'

// One uploaded sample (a file, or FASTQ lanes of one sample) in a list: sample ID
// in mono, type, upload day, and either an Add affordance, a checkbox or a state.
// A row that cannot be analysed is disabled with candidateInfo's reason: "R2
// missing", "Not verified", or "Stuck verifying" for an upload still verifying a
// month on.

const CandidateRow = ({ candidate, selectable, checked, added, analysed, onToggle, onAdd }) => {
  const theme = useTheme()
  const info = candidateInfo(candidate)
  const disabled = !!info.disabledReason || added
  const handleClick = () => {
    if (disabled) return
    if (selectable) onToggle(candidate)
    else onAdd(candidate)
  }

  return (
    <ButtonBase
      component="div"
      role={selectable ? 'checkbox' : 'button'}
      aria-checked={selectable ? checked : undefined}
      aria-disabled={disabled}
      onClick={handleClick}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: selectable ? '36px minmax(0, 1fr) 190px 84px 112px' : 'minmax(0, 1fr) 190px 84px 112px',
        alignItems: 'center',
        columnGap: 2,
        minHeight: 44,
        px: 1,
        py: 0.5,
        borderRadius: `${theme.shape.borderRadius}px`,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': { backgroundColor: disabled ? 'transparent' : 'action.hover' },
      }}
    >
      {selectable && (
        <Checkbox
          size="small"
          checked={!!checked || !!added}
          disabled={disabled}
          tabIndex={-1}
          sx={{ p: 0.5 }}
          inputProps={{ 'aria-hidden': true }}
        />
      )}
      <Box sx={{ minWidth: 0, opacity: disabled && !added ? 0.6 : 1 }}>
        <FileNameText name={candidate.key} variant="plain" maxLines={1} sx={{ fontSize: 14, fontWeight: 500 }} />
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
        {info.typeLabel}
        {analysed ? ' · analysed' : ''}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }} noWrap>
        {shortDay(info.uploadedAt)}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75 }}>
        {added ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Added</Typography>
        ) : info.disabledReason ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
            {info.disabledReason}
          </Typography>
        ) : info.state === 'verifying' ? (
          <>
            <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Verifying</Typography>
          </>
        ) : (
          !selectable && (
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}
            >
              <AddIcon sx={{ fontSize: 16, mr: 0.25 }} />
              Add
            </Typography>
          )
        )}
      </Box>
    </ButtonBase>
  )
}

export default React.memo(CandidateRow)
